import { injectable } from 'inversify'

export const ThemeModes = ['light', 'dark', 'system'] as const
export type ThemeMode = (typeof ThemeModes)[number]

export type ThemeModeOption = {
  mode: ThemeMode
  /**
   * A translation key, not a label. Config owns *what modes exist*; what they
   * are called in the user's language is resolved by ThemePresenter through
   * I18nRepository, so adding a locale never touches this file.
   */
  labelKey: string
}

/**
 * Declarative half of theming — see the note on TranslationsConfig.
 * ThemeRepository owns which mode is selected; this owns what modes exist,
 * what they are called, and in what order the toggle cycles them.
 */
@injectable()
export class ThemeConfig {
  readonly defaultMode: ThemeMode = 'system'

  /** Also the cycle order the toggle button walks, in array order. */
  readonly modes: ThemeModeOption[] = [
    { mode: 'light', labelKey: 'theme.light' },
    { mode: 'dark', labelKey: 'theme.dark' },
    { mode: 'system', labelKey: 'theme.system' },
  ]

  readonly darkQuery = '(prefers-color-scheme: dark)'

  isSupported(mode: string): mode is ThemeMode {
    return this.modes.some((option) => option.mode === mode)
  }

  labelKeyFor(mode: ThemeMode): string {
    return this.modes.find((option) => option.mode === mode)!.labelKey
  }

  /** Next mode in cycle order — the toggle's only navigation rule. */
  nextAfter(mode: ThemeMode): ThemeMode {
    const index = this.modes.findIndex((option) => option.mode === mode)
    return this.modes[(index + 1) % this.modes.length].mode
  }
}
